/**
 * The attendance vocabulary: nine badges, twelve reasons, and the one rule about a
 * number that is not there.
 *
 * A module beside `timer/` rather than inside `screens/attendance/`, for the reason
 * Task 42 gave that directory: two screens read `GET /attendance` — the employee's
 * own card on `/my-activity` and the grid on `/attendance` — and a vocabulary that
 * lived inside one of them would be copied into the other, which is how two screens
 * come to disagree about what `late_minutes: null` means.
 *
 * ══════════════════════════════════════════════════════════════════════════════
 *  A WITHHELD METRIC IS NOT A ZERO.
 *
 *  `docs/DATA_MODEL.md` D-22: the four shift-relative metric columns of
 *  `wp_work_sessions` are `NOT NULL DEFAULT 0`, so **in the table** a value the
 *  system declined to publish and a value it measured as zero are the same byte.
 *  On the wire they are not: `calc.ts` sends `null` and names the reason in
 *  `withheld`, and its invariant is `value === null ⟺ metric ∈ withheld`.
 *
 *  So `readMetric` below is the only way this product turns one of those fields
 *  into something a person reads, and it has **no branch that produces a zero from
 *  an absence**. `formatDurationMinutes(null)` returns "0 ph" — it is documented to
 *  clamp — which is correct for a duration and catastrophic for a metric. The
 *  clamp is why this function exists rather than a `?? 0` at each call site.
 *
 *  What a manager sees if this is got wrong: an employee who did nothing, where the
 *  truth is an employee nobody could see.
 * ══════════════════════════════════════════════════════════════════════════════
 */

import type { Translate } from '../i18n';
import type { MessageKey } from '../i18n/messages';
import { formatDurationMinutes } from '../timer/elapsed';
import type { BadgeTone } from '../states/Badge';
import type { IconName, Severity } from '../states/icons';
import type {
  AttendanceBadgeStatus,
  AttendanceMetricName,
  AttendanceReason,
  SessionCompleteness,
  WithheldMetrics,
} from '../api/types';

/** One row of `web/shapes/attendance.md` §5, and of `DESIGN.md` §4 rule 4. */
export interface StatusPresentation {
  /** Chooses the colour token. The third channel — never the first, never the only. */
  readonly severity: Severity;
  /** The silhouette. Distinct across all nine; asserted with colour stripped. */
  readonly icon: IconName;
  /** The words. Mandatory — `Badge` has no prop that renders without them. */
  readonly labelKey: MessageKey;
  /** The tooltip spec 01 §19 asks for on jargon. Additional to the label, never instead. */
  readonly helpKey: MessageKey;
  /**
   * How loudly to paint it. Absent means `'severity'` — eight of the nine are a
   * statement about somebody's day and earn their colour. The exception is named on
   * its own row below.
   */
  readonly tone?: BadgeTone;
}

/**
 * The table. Nine entries for nine values, exhaustive over `AttendanceBadgeStatus`
 * by its type — a value the server invents cannot fall through to a colour with no
 * words, because there is no fallback branch anywhere that reads this map.
 *
 * Two severity decisions, from `web/shapes/attendance.md` §5:
 *
 *  - **`absent` is `high` and `data_incomplete` is `medium`.** `absent` is the only
 *    verdict that accuses somebody, and `calc.ts` reaches it only from a `complete`
 *    day. `data_incomplete` is a statement about the fleet, so it is quieter than
 *    the statement about the person.
 *  - **`on_time` is `low`, not a celebration.** Spec 01 §4.1.4 forbids gamifying
 *    surveillance: a person who arrived on time gets an outlined circle and two
 *    words, the same weight as everything else in the column.
 */
export const STATUS_PRESENTATION: Readonly<Record<AttendanceBadgeStatus, StatusPresentation>> = {
  on_time: {
    severity: 'low',
    icon: 'circle-check',
    labelKey: 'attendance.status.on_time',
    helpKey: 'attendance.status.help.on_time',
  },
  late: {
    severity: 'medium',
    icon: 'arrow-right-to-line',
    labelKey: 'attendance.status.late',
    helpKey: 'attendance.status.help.late',
  },
  early_leave: {
    severity: 'medium',
    icon: 'arrow-left-to-line',
    labelKey: 'attendance.status.early_leave',
    helpKey: 'attendance.status.help.early_leave',
  },
  late_and_early: {
    severity: 'high',
    icon: 'fold-horizontal',
    labelKey: 'attendance.status.late_and_early',
    helpKey: 'attendance.status.help.late_and_early',
  },
  absent: {
    severity: 'high',
    icon: 'ban',
    labelKey: 'attendance.status.absent',
    helpKey: 'attendance.status.help.absent',
  },
  off_day: {
    severity: 'info',
    icon: 'moon',
    labelKey: 'attendance.status.off_day',
    helpKey: 'attendance.status.help.off_day',
  },
  leave: {
    severity: 'info',
    icon: 'calendar-check',
    labelKey: 'attendance.status.leave',
    helpKey: 'attendance.status.help.leave',
  },
  data_incomplete: {
    severity: 'medium',
    icon: 'triangle-alert',
    labelKey: 'attendance.status.data_incomplete',
    helpKey: 'attendance.status.help.data_incomplete',
  },
  /*
   * The one row painted neutral. `not_configured` says **the company has not set a
   * shift for this day** — it is a statement about configuration, not about the
   * person in the row, and it lands on every employee at once (a company with no
   * roster shows it six times out of six). An amber chip on every row of a table is
   * a table with no signal left for the row that really is late, so this keeps its
   * glyph and its words and gives up the colour (Luật Màu Có Nghĩa). `severity`
   * stays `medium` because that is still what it classifies as — `data-severity`
   * carries it, and `/overview`'s exception list still counts it.
   */
  not_configured: {
    severity: 'medium',
    icon: 'sliders-horizontal',
    labelKey: 'attendance.status.not_configured',
    helpKey: 'attendance.status.help.not_configured',
    tone: 'neutral',
  },
};

/** The thirteen `AttendanceReason` values, each with a sentence rather than a code. */
export const REASON_KEY: Readonly<Record<AttendanceReason, MessageKey>> = {
  no_shift_configured: 'attendance.reason.no_shift_configured',
  not_a_working_day: 'attendance.reason.not_a_working_day',
  no_session_row: 'attendance.reason.no_session_row',
  agent_data_missing: 'attendance.reason.agent_data_missing',
  no_enrolled_device: 'attendance.reason.no_enrolled_device',
  incomplete_data: 'attendance.reason.incomplete_data',
  day_in_progress: 'attendance.reason.day_in_progress',
  no_arrival_observed: 'attendance.reason.no_arrival_observed',
  no_departure_observed: 'attendance.reason.no_departure_observed',
  overtime_requires_manual_claim: 'attendance.reason.overtime_requires_manual_claim',
  overtime_not_credited_on_incomplete_data:
    'attendance.reason.overtime_not_credited_on_incomplete_data',
  overtime_rule_unrecognised: 'attendance.reason.overtime_rule_unrecognised',
  stale_source_version: 'attendance.reason.stale_source_version',
};

/** The five values of `completeness.status`, each with its own sentence. */
export const COMPLETENESS_KEY: Readonly<Record<SessionCompleteness, MessageKey>> = {
  complete: 'attendance.completeness.complete',
  partial: 'attendance.completeness.partial',
  missing: 'attendance.completeness.missing',
  no_data: 'attendance.completeness.no_data',
  no_session_row: 'attendance.completeness.no_session_row',
};

/**
 * What one metric field says. Three outcomes, and **none of them is a zero the
 * caller invented**.
 *
 *  `measured`  — a number the server published. `0` here is a real zero.
 *  `withheld`  — `null` **and** a reason. The reason is rendered in words.
 *  `unexplained` — `null` with no reason. The server's `assertExplained` makes this
 *                unreachable, and it is handled anyway: an absence nobody explained
 *                is still an absence, and inventing a zero for it would be exactly
 *                the failure the explained case is protecting against.
 */
export type MetricReading =
  | { readonly kind: 'measured'; readonly minutes: number }
  | { readonly kind: 'withheld'; readonly reason: AttendanceReason }
  | { readonly kind: 'unexplained' };

export function readMetric(
  value: number | null | undefined,
  withheld: WithheldMetrics | undefined,
  name: AttendanceMetricName,
): MetricReading {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return { kind: 'measured', minutes: value };
  }
  const reason = withheld?.[name];
  if (reason === undefined) return { kind: 'unexplained' };
  return { kind: 'withheld', reason };
}

/**
 * The same three outcomes for a duration that is not one of the four named metrics
 * — `online_minutes` and its neighbours, which `durationsOf` returns as `null` for a
 * day with no session row and which carry no `withheld` entry of their own.
 *
 * They get the **day's** reason instead, when it has one, because on such a day the
 * reason the durations are absent is the reason the day is: there was no row.
 */
export function readDuration(
  value: number | null | undefined,
  dayReason: AttendanceReason | null,
): MetricReading {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return { kind: 'measured', minutes: value };
  }
  if (dayReason === null) return { kind: 'unexplained' };
  return { kind: 'withheld', reason: dayReason };
}

/**
 * A reading, rendered. **There is no branch here that prints a zero for an absence**
 * — which is the whole reason `MetricReading` exists and the reason this is the only
 * function on either attendance screen that turns one into text.
 *
 * Shared rather than written per component, because "what does a withheld metric
 * look like" is exactly the kind of decision that drifts when two screens each own a
 * copy of it, and the two screens here are the employee's own card and the grid a
 * manager reads about them.
 */
export function renderReading(
  reading: MetricReading,
  durations: { readonly hour: string; readonly minute: string },
  t: Translate,
): string {
  if (reading.kind === 'measured') return formatDurationMinutes(reading.minutes, durations);
  if (reading.kind === 'withheld') {
    return t('k3.metric.withheld', { reason: t(REASON_KEY[reading.reason]) });
  }
  return t('k3.metric.unexplained');
}

/**
 * The newest `last_event_received_at` across a page of grid cells, or `null`.
 *
 * The freshest thing the fleet is known to have said about this window, and the only
 * agent-side timestamp these screens can read: `GET /devices` carries real heartbeats
 * and needs `workforce.manage_devices`, which the attendance personas do not
 * necessarily hold, so a second request for it would refuse for half the audience.
 *
 * Shared because two callers derive it — the screen's `LastSync` line and the
 * calendar's `DataDelayed` — and two copies of one derivation are two things that
 * can disagree about how fresh the same page is.
 */
export function newestEventAt(
  rows: readonly {
    readonly shift: { readonly source: { readonly last_event_received_at: string | null } };
  }[],
): string | null {
  let newest: string | null = null;
  for (const row of rows) {
    const value = row.shift.source.last_event_received_at;
    if (value === null) continue;
    if (newest === null || value > newest) newest = value;
  }
  return newest;
}
